/**
 * @file: useInspectionReport.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  InspectionResultFormatter,
  toMediaObject,
} from '@euler/app/flows/report/functions';
import {
  getDefectiveLevel,
  getDefectiveLevelTokenByValue,
} from '@euler/functions/getDefectiveLevel';
import { DefectiveLevel, MediaObject } from '@euler/model';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import {
  AbnormalLevel,
  SeverityLevel,
  SiteInspectionType,
} from '@euler/model/enum';
import {
  InspectionReportItem,
  InspectionReportItemType,
  ReportProductReferralItemConfirmation,
  VehicleReport,
} from '@euler/model/report';
import { arr2groupmap } from '@euler/utils/array';
import { comparator } from 'ramda';
import { useMemo } from 'react';

export type ItemModel = {
  name: string;
  label: string;
  type: InspectionReportItemType;
  defectiveClass?: string;
  defectiveLevel: DefectiveLevel;
  description?: string;
  valueRange?: string;
  remark?: string;
};

// https://stackoverflow.com/questions/64181463/how-can-i-reference-to-a-type-which-is-part-of-a-union-type-in-typescript
export type InspectionResultCellType<
  T extends InspectionResultCellModel['type'],
  Model = InspectionResultCellModel,
> = Model extends { type: T } ? Model : never;

export type InspectionResultCellModel = { id: string } & (
  | {
      type: 'score';
      score: number;
    }
  | {
      type: 'group-header';
      title: string;
      description: string;
      defectiveLevel: DefectiveLevel;
      count: number;
      index: number;
    }
  | {
      type: 'section';
      index: number;
      title: string;
      tag?: string;
      defectiveLevel: DefectiveLevel;
      medias: MediaObject[];
      items: ItemModel[];
      productConfirmations: ReportProductReferralItemConfirmation[] | undefined;
    }
);

type DefectiveGroupInfo = {
  title: string;
  description: string;
};

export const DefectiveGroupInfoMap: {
  [p in DefectiveLevel]: DefectiveGroupInfo;
} = {
  [DefectiveLevel.Fine]: {
    title: '正常结果',
    description: '以下部位经检测均正常, 请继续持续关注爱车健康状况',
  },
  [DefectiveLevel.Defective]: {
    title: '轻微异常',
    description: '轻微异常或损坏, 可修(换)可不修(换), 不影响正常行驶',
  },
  [DefectiveLevel.Warning]: {
    title: '建议处理',
    description:
      '明显异常或损坏, 不影响当前行驶, 不会造成更严重后果(非行驶安全部位)',
  },
  [DefectiveLevel.Urgent]: {
    title: '急需处理',
    description:
      '明显异常或损坏, 影响正常行驶(影响行驶安全)或后续增加维修成本部件',
  },
};

export type InspectionResultGroup = {
  defectiveLevel: DefectiveLevel;
  sections: InspectionResultSection[];
};

export type InspectionResultSection = {
  title: string;
  tag?: string;
  defectiveLevel: DefectiveLevel;
  items: InspectionReportItem[];
};

export function useInspectionReportModels(
  report: VehicleReport,
  options?: {
    includeFineInDefective?: boolean;
    inspectionResultsOnly?: boolean;
    flowId?: string;
    faultLikeSubject?: FaultInspectionItem;
  },
): {
  cells: InspectionResultCellModel[];
} {
  return useMemo(() => {
    const cells: InspectionResultCellModel[] = [];

    // score cell
    if (!options?.inspectionResultsOnly && options?.faultLikeSubject == null) {
      cells.push({
        id: 'score-cell',
        type: 'score',
        score: report.inspection?.score ?? report.score ?? 0,
      });
    }

    const groups = resultGroupsByDefectiveLevel(report, {
      flowId: options?.flowId,
      includeFineInDefective: options?.includeFineInDefective,
      faultLikeSubject: options?.faultLikeSubject,
    });

    let groupIndex = 0;

    for (const group of groups) {
      // group header cell
      const groupToken = getDefectiveLevelTokenByValue(group.defectiveLevel);
      const groupInfo = DefectiveGroupInfoMap[group.defectiveLevel];
      cells.push({
        type: 'group-header',
        id: `group/${groupToken}/header`,
        title: groupInfo.title,
        description: groupInfo.description,
        count: group.sections.length,
        defectiveLevel: group.defectiveLevel,
        index: groupIndex++,
      });

      group.sections.forEach((section, index) => {
        const medias: MediaObject[] = [];
        const items: ItemModel[] = [];

        for (const item of section.items) {
          medias.push(...item.medias.map(x => toMediaObject(x)));
          const name =
            InspectionResultFormatter.getInspectedItemDisplayName(item);
          const [label, valueRange] =
            InspectionResultFormatter.formatLabel(item);
          const defectiveLevel = getDefectiveLevel(
            item.abnormalLevel,
            item.severityLevel,
          );

          let description: string | undefined = undefined;
          let remark: string | undefined = undefined;

          if (item.type === InspectionReportItemType.CustomIssue) {
            // const influenceFactor = matchCustomIssueInfluenceFactor(
            //   item.siteId == null
            //     ? item.siteInfluenceFactor
            //     : item.siteItemInfluenceFactor,
            //   item.defectiveClass,
            // );
            description = item.resultRemark;
          } else {
            description = item.abnormalDescription;
            remark = item.resultRemark ?? undefined;
          }

          items.push({
            type: item.type,
            name,
            label: label ?? '',
            valueRange: valueRange ?? undefined,
            defectiveLevel,
            defectiveClass: item.defectiveClass,
            description: description?.trim() ?? '暂无相关故障影响说明',
            remark,
          });
        }

        // section cell
        cells.push({
          type: 'section',
          id: `group/${groupToken}/section/${index}`,
          index,
          title: section.title,
          tag: section.tag,
          defectiveLevel: section.defectiveLevel,
          medias,
          items,
          productConfirmations:
            section.defectiveLevel !== DefectiveLevel.Fine
              ? report.productReferrals?.find(x => x.subject === section.title)
                  ?.confirmations
              : undefined,
        });
      });
    }

    return { cells };
  }, [
    options?.faultLikeSubject,
    options?.flowId,
    options?.includeFineInDefective,
    options?.inspectionResultsOnly,
    report,
  ]);
}

// divide the inspection result items to groups by defective level
// with each group further divided by maintenance advice or item name (for
// normal inspection results).
function resultGroupsByDefectiveLevel(
  report: VehicleReport,
  options?: {
    flowId?: string;
    faultLikeSubject?: FaultInspectionItem;
    includeFineInDefective?: boolean;
  },
): InspectionResultGroup[] {
  const groups: InspectionResultGroup[] = [];

  const sectionFor = (defectiveLevel: DefectiveLevel, title: string) => {
    let group = groups.find(x => x.defectiveLevel === defectiveLevel);
    if (!group) {
      group = {
        defectiveLevel,
        sections: [],
      };
      groups.push(group);
    }
    let section = group.sections.find(x => x.title === title);
    if (!section) {
      section = {
        title,
        items: [],
        defectiveLevel: DefectiveLevel.Fine, // default defective level
      };
      group.sections.push(section);
    }
    return section;
  };

  const items =
    report.inspection?.items.filter(
      x =>
        x.inspectionType === SiteInspectionType.Default &&
        (options?.flowId == null || x.flowId === options?.flowId) &&
        (options?.faultLikeSubject == null ||
          options.faultLikeSubject.relatedInspections?.some(
            relatedInspection => {
              if (x.type === InspectionReportItemType.Normal) {
                return (
                  relatedInspection.type === 'item' &&
                  relatedInspection.siteCode === x.siteCode &&
                  relatedInspection.itemName === x.siteItemName
                );
              }
              if (x.type === InspectionReportItemType.CustomIssue) {
                return (
                  relatedInspection.type === 'custom-issue' &&
                  (relatedInspection.siteCode
                    ? relatedInspection.siteCode === x.siteCode
                    : relatedInspection.siteName === x.siteName) &&
                  (relatedInspection.itemName ?? '') === (x.siteItemName ?? '')
                );
              }
              return false;
            },
          )),
    ) ?? [];

  // first group the items by site.
  const defectiveItems: (typeof items)[0][] = [];
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const groupsBySiteMap = arr2groupmap(items, x => x.siteName || '未知部位');

  const isFine = (item: InspectionReportItem) =>
    item.abnormalLevel === AbnormalLevel.Fine;

  const defectiveSiteIdSet = new Set(
    items.filter(x => !isFine(x) && x.siteId).map(x => x.siteId!),
  );

  const addFineItems = (title: string, list: InspectionReportItem[]) => {
    list = list.filter(
      x =>
        options?.includeFineInDefective ||
        !x.siteId ||
        !defectiveSiteIdSet.has(x.siteId),
    );
    if (!list.length) return;
    const section = sectionFor(DefectiveLevel.Fine, title);
    section.items.push(...list);
  };

  for (const [siteName, list] of groupsBySiteMap) {
    for (const item of list) {
      if (isFine(item)) {
        addFineItems(siteName, [item]);
      } else {
        defectiveItems.push(item);
      }
    }
  }

  const groupMapByTitle = arr2groupmap(
    defectiveItems,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    x => x.label || x.maintenanceAdvice || '',
  );
  for (const [title, list] of groupMapByTitle) {
    const defectiveLevel: DefectiveLevel = list.some(
      x => x.severityLevel === SeverityLevel.Danger,
    )
      ? DefectiveLevel.Urgent
      : list.some(x => x.severityLevel === SeverityLevel.Warning)
      ? DefectiveLevel.Warning
      : DefectiveLevel.Defective;
    const section = sectionFor(defectiveLevel, title);
    section.defectiveLevel = defectiveLevel;
    section.items.push(...list);
    section.items.sort((x, y) => y.severityLevel - x.severityLevel);
  }

  const fineGroup = groups.find(x => x.defectiveLevel === DefectiveLevel.Fine);
  if (fineGroup) {
    // sort the sections in the group by media counts
    const cardinality = (list: InspectionReportItem[]) => {
      return list.reduce((res, item) => res + (item.medias?.length ?? 0), 0);
    };
    fineGroup.sections.sort((x, y) => {
      const [a, b] = [cardinality(x.items), cardinality(y.items)];
      return a > b ? -1 : a < b ? 1 : 0;
    });
  }

  groups.sort(comparator((x, y) => x.defectiveLevel > y.defectiveLevel));

  return groups;
}
